import * as Yup from 'yup'
import { isValid as isValidCpf } from '@fnando/cpf'
import { DateTime } from 'luxon'

/*
  Cartão do sinal do agendamento online.

  Nada aqui prova que o cartão existe — quem diz isso é o Asaas, e a recusa dele chega na
  resposta da cobrança. O que estas regras evitam é gastar uma tentativa de cobrança com
  erro de digitação óbvio, que na maquininha do Asaas conta como transação recusada.

  O CPF é do **titular do cartão**, e por isso não sai do `scheduleCustomerValidator`: quem
  agenda e quem paga não são sempre a mesma pessoa, e é o do titular que o Asaas exige em
  `creditCardHolderInfo`.
*/
const onlinePaymentCardValidator = Yup.object ().shape ({
  number: Yup.string ()
    .required ('Digite o número do cartão')
    /*
      O Cleave insere espaços a cada quatro dígitos, então o comprimento é medido sobre os
      números. A faixa cobre de Diners (14) a Maestro (19).
    */
    .test ('validLength', 'Número de cartão inválido', value => {
      const digits = (value ?? '').replace (/\D/g, '')
      return digits.length >= 13 && digits.length <= 19
    }),

  holderName: Yup.string ()
    .required ('Digite o nome impresso no cartão')
    .min (3, 'O nome deve conter no mínimo 3 caracteres')
    .max (64, 'O nome deve conter até 64 caracteres'),

  expiry: Yup.string ()
    .required ('Digite a validade')
    .test ('validExpiry', 'Validade inválida ou vencida', value => {
      const digits = (value ?? '').replace (/\D/g, '')

      if (digits.length < 4) return false

      const month = Number (digits.slice (0, 2))

      if (month < 1 || month > 12) return false

      /*
        O cartão vale até o último instante do mês impresso nele, e não até o primeiro dia:
        um cartão "08/26" ainda passa no dia 31 de agosto de 2026.
      */
      const expiresAt = DateTime.fromObject ({
        month, year: Number (`20${digits.slice (2, 4)}`)
      }).endOf ('month')

      return expiresAt >= DateTime.now ()
    }),

  // Amex usa quatro dígitos; o resto das bandeiras, três.
  ccv: Yup.string ()
    .required ('Digite o código de segurança')
    .matches (/^\d{3,4}$/, 'O código tem 3 ou 4 números'),

  cpf: Yup.string ()
    .required ('Digite o CPF do titular do cartão')
    .test ('validCpf', 'Digite um CPF válido', value => isValidCpf (value ?? '')),

  /*
    CEP e número são do endereço de cobrança do cartão, e o Asaas recusa a transação sem
    eles. Não dá para reaproveitar o endereço do salão nem deixar em branco: é com estes
    dois campos que o antifraude confere o portador.
  */
  postalCode: Yup.string ()
    .required ('Digite o CEP do titular')
    .test ('validPostalCode', 'Digite um CEP válido', value => (
      (value ?? '').replace (/\D/g, '').length === 8
    )),

  addressNumber: Yup.string ()
    .required ('Digite o número do endereço')
    .max (10, 'O número deve conter até 10 caracteres')
})

export default onlinePaymentCardValidator
